/**
 * Rewrite legacy unit-pricing blog markdown to AI Employee plans.
 * Used by adapt-blog-for-siteglue and by the content update script.
 */

import {
  siteGlueAiEmployeePricingCtaMarkdown,
  siteGlueAiEmployeePricingExamplePhoneScheduling,
  siteGlueAiEmployeePricingFooterLinksMarkdown,
  siteGlueAiEmployeePricingSectionMarkdown,
  siteGlueAiEmployeePricingShortLine,
  SITEGLUE_BLOG_AGENTS_URL,
  SITEGLUE_BLOG_PRICING_URL,
  SITEGLUE_BLOG_SIGNUP_URL,
} from "~/lib/siteglue/blog-pricing-copy";
import { SITEGLUE_SIGNUP_CTA_LABEL } from "~/lib/siteglue/cta-config";

const SIGNUP_LINK_MARKDOWN = `[${SITEGLUE_SIGNUP_CTA_LABEL}](${SITEGLUE_BLOG_SIGNUP_URL})`;
const LEGACY_SIGNUP_CTA_LABEL = "Get SiteGlue Free";

const UNIT_SECTION_RE =
  /## Unit-based pricing[^\n]*\n[\s\S]*?(?=\n## |\n---\n|\n\[Try It Free\]|\n\[Browse |\n\[Documentation\]|\nStart with free|\Z)/;

const TRY_IT_FREE_LINK_RE =
  /\[Try It Free\]\(https?:\/\/(?:app\.)?easygpt\.builders\/agents\/hire\/custom[^)]*\)/gi;

const START_WITH_FREE_CHAT_RE =
  /Start with free Live Chat[^.]*\([^)]*\)\./gi;

const PRICING_CALCULATOR_LINK_RE =
  /\[(?:See )?pricing calculator\]\(https?:\/\/(?:app\.)?easygpt\.builders\/pricing[^)]*\)/gi;

const BROWSE_AGENT_TEMPLATES_RE =
  /\[Browse agent templates\]\(https?:\/\/(?:app\.)?easygpt\.builders\/agents[^)]*\)/gi;

const BROWSE_AGENTS_RE =
  /\[Browse agents\]\(https?:\/\/(?:app\.)?easygpt\.builders\/agents[^)]*\)/gi;

/** Replace outdated unit-pricing language with SiteGlue Agent plan copy. */
export function rewriteBlogPricingMarkdown(content: string): string {
  let next = content;

  // Refresh any existing plan section to the latest helper output
  const existingPlanSection =
    /## (?:AI Employee|SiteGlue Agent) pricing plans\n[\s\S]*?(?=\n## |\n---\n|\n\[(?:Get SiteGlue Free|Get Started Free)\]|\n\[Browse |\n\[Documentation\]|\nHire your first|\nSet up your|\nStart with |\Z)/;
  if (existingPlanSection.test(next)) {
    next = next.replace(existingPlanSection, () => `${siteGlueAiEmployeePricingSectionMarkdown()}\n\n`);
  } else if (UNIT_SECTION_RE.test(next)) {
    next = next.replace(UNIT_SECTION_RE, () => `${siteGlueAiEmployeePricingSectionMarkdown()}\n\n`);
  }

  next = next.replace(START_WITH_FREE_CHAT_RE, () => siteGlueAiEmployeePricingCtaMarkdown());
  next = next.replace(TRY_IT_FREE_LINK_RE, SIGNUP_LINK_MARKDOWN);
  next = next.replace(
    new RegExp(`\\[${LEGACY_SIGNUP_CTA_LABEL}\\]\\([^)]+\\)`, "g"),
    SIGNUP_LINK_MARKDOWN,
  );
  next = next.replace(PRICING_CALCULATOR_LINK_RE, `[See pricing](${SITEGLUE_BLOG_PRICING_URL})`);
  next = next.replace(BROWSE_AGENT_TEMPLATES_RE, `[Browse Agents](${SITEGLUE_BLOG_AGENTS_URL})`);
  next = next.replace(BROWSE_AGENTS_RE, `[Browse Agents](${SITEGLUE_BLOG_AGENTS_URL})`);

  // Narrative / short callouts
  next = next.replace(
    /\*\*Pricing:\*\* Unit-based[^\n]*/gi,
    `**Pricing:** ${siteGlueAiEmployeePricingShortLine()}`,
  );
  next = next.replace(
    /\*\*Pricing:\*\* Live Chat free[^\n]*/gi,
    `**Pricing:** ${siteGlueAiEmployeePricingShortLine()}`,
  );

  next = next.replace(/unit-based pricing/gi, "SiteGlue Agent plan pricing");
  next = next.replace(
    /unit-based or clearly itemized; avoid opaque bundles/gi,
    "Clear SiteGlue Agent plans (Intern → Enterprise); avoid opaque bundles",
  );
  next = next.replace(
    /Clone Official and Moonlight templates, configure profile \/ responsibilities \/ training \/ tools, and go live with (?:unit-based pricing|AI Employee plan pricing|SiteGlue Agent plan pricing)\./gi,
    "Set up a SiteGlue Agent, configure channels and knowledge, and go live with transparent plan pricing—always with a Human-in-the-Loop™.",
  );
  next = next.replace(/Moonlight templates/gi, "SiteGlue Agent roles");
  next = next.replace(/Official and Moonlight templates/gi, "SiteGlue Agent roles");
  next = next.replace(/agent templates/gi, "SiteGlue Agents");
  next = next.replace(/Agent templates/gi, "SiteGlue Agents");

  // Flat-fee comparison narratives that still cite unit stacks
  next = next.replace(
    /## Why ~\$80–\$170\/month beats a \$995 flat fee/gi,
    "## Why Associate or Professional beats a $995 flat fee",
  );
  next = next.replace(
    /- You start free with Live Chat and expand channels when ready\n- Phone and SMS include free minutes\/messages, then clear per-unit rates\n- Humans stay available for transfers and inbox follow-up\n- No long-term contract required to "unlock" basic coverage/gi,
    `- Begin on Intern (FREE website chat) and expand channels when ready
- Associate ($99/mo) and Professional ($299/mo) include clear conversation credits—not opaque minute stacks
- Humans stay available for transfers and inbox follow-up
- No long-term contract required to unlock basic coverage`,
  );
  next = next.replace(
    /\*\*Transparent unit prices\*\* you can model before you hire/gi,
    "**Transparent SiteGlue Agent plans** you can model before you deploy",
  );
  next = next.replace(/~\$80–\$170\/month/gi, "Associate ($99/mo) or Professional ($299/mo)");
  next = next.replace(/\$80–\$170/gi, "$99–$299");
  next = next.replace(
    /starts around \*\*\$80\/month\*\* before overage minutes, not a four-figure bundle\./gi,
    "typically lands on **Associate ($99/mo)** or **Professional ($299/mo)**—not a four-figure opaque bundle.",
  );
  next = next.replace(
    /typically starts around \*\*\$80\/month\*\* before phone overage — with free Live Chat to prove quality first\./gi,
    "typically starts on **Associate ($99/mo)** or **Professional ($299/mo)**—with free Intern website chat to prove quality first.",
  );
  next = next.replace(
    /\*\*Inbound GPT starter \(Live Chat \+ Phone \+ EMR\):\*\* ~\$80\/month base, plus phone minutes after the included 20\. Even with moderate overage, many teams land around \*\*~\$170\/month\*\* — still a fraction of a bundled subscription\./gi,
    "**SiteGlue Agent plans:** Intern (FREE) for website chat; Associate ($99/mo) for phone and email; Professional ($299/mo) for full-channel coverage—still a fraction of a bundled subscription.",
  );
  next = next.replace(
    /\*\*Inbound GPT \(Live Chat \+ Phone \+ EMR\):\*\* ~\$80\/month base\. With moderate phone usage after included minutes, many practices land near \*\*~\$170\/month\*\* — still far below a bundled AI receptionist subscription\./gi,
    "**SiteGlue Agent plans:** Intern (FREE) for website chat; Associate ($99/mo) or Professional ($299/mo) when you need phone and full-channel coverage—still far below a bundled AI receptionist subscription.",
  );
  next = next.replace(
    /Live Chat, Phone, and Juvonno EMR typically starts around \*\*\$80\/month\*\* before phone overage — with free Live Chat to prove value first\./gi,
    "Phone coverage plus a verified scheduling integration typically starts on **Associate ($99/mo)** or **Professional ($299/mo)**—with free Intern website chat to prove value first.",
  );
  next = next.replace(
    /Inbound GPT takes a different approach: \*\*unit-based channels and tools\*\*, free Live Chat to start,/gi,
    "SiteGlue takes a different approach: **SiteGlue Agent plans** with clear channel tiers, free Intern chat to start,",
  );
  next = next.replace(
    /how unit-based inbound agents keep humans in the loop/gi,
    "how SiteGlue Agent plans keep humans in the loop",
  );
  next = next.replace(
    /Inbound GPT is unit-based: start with free Live Chat; add Phone \(\$30\/mo\) and tools as needed\./gi,
    "SiteGlue uses SiteGlue Agent plans: begin with free Intern chat; add Associate ($99/mo) for phone and tools as needed.",
  );
  next = next.replace(
    /to a unit-based agent stack you can start free/gi,
    "to a SiteGlue Agent plan you can start free on Intern",
  );
  next = next.replace(
    /how unit-based AI agents compare when humans stay in the loop/gi,
    "how SiteGlue Agent plans compare when humans stay in the loop",
  );
  // Catch-all leftovers for older ROI math
  next = next.replace(/~\$80\/month/gi, "$99/mo (Associate)");
  next = next.replace(/\*\*\$80\/month\*\*/gi, "**$99/month (Associate)**");
  next = next.replace(/~\$170\/month/gi, "$299/mo (Professional)");
  next = next.replace(/\*\*~\$170\/month\*\*/gi, "**$299/month (Professional)**");
  next = next.replace(/Phone \(\$30\/mo\)/gi, "Associate ($99/mo)");
  next = next.replace(/overage minutes/gi, "conversation credits");
  next = next.replace(/phone overage/gi, "higher plan volume");
  next = next.replace(/phone minutes after the included 20/gi, "conversation credits on your plan");
  next = next.replace(/unit-based/gi, "plan-based");

  // Specific outdated SiteGlue-native copy (posts 15/16)
  next = next.replace(
    /A practice with live chat and phone coverage can start for as little as \$30\/mo with platforms like SiteGlue AI, which charges \$30\/mo for phone calls \(first 20 minutes free, then \$0\.50\/min\) and \$0\/mo for live chat\./g,
    "A practice that needs website chat and phone coverage can start on SiteGlue Associate ($99/mo) after hiring free on Intern for chat-only, with Human-in-the-Loop™ on every plan.",
  );
  next = next.replace(
    /For small businesses, unit pricing tends to be the most cost-effective\./gi,
    "For small businesses, transparent SiteGlue Agent plans tend to be the most cost-effective.",
  );
  next = next.replace(
    /- \*\*Usage-based \/ unit pricing\*\* — pay per channel, per conversation, or per outcome/g,
    "- **SiteGlue Agent plans** — transparent monthly tiers (Intern, Associate, Professional, Enterprise) with included conversation credits",
  );

  next = next.replace(
    /Activate your channels\. Live chat, voice, and email are free\. SMS, phone, and video calls are pay-per-use\. Toggle on what makes sense for your business\./g,
    "Choose your communication channels based on your plan. Intern covers website chat; Associate adds phone, email, and voice; Professional unlocks full-channel coverage including SMS, WhatsApp, and Messenger.",
  );
  next = next.replace(
    /\*\*How much does it cost\?\*\*\nLive chat, voice, email, Messenger, and WhatsApp are free\. Phone calls start at \$0\.50\/min after the first 20 free minutes\. SMS starts at \$0\.10\/message after the first 100 free\. You pay only for what you use — no subscription contracts\./g,
    `**How much does it cost?**\n${siteGlueAiEmployeePricingShortLine()} Always a Human-in-the-Loop™—no opaque per-minute add-on maze.`,
  );

  // Generic leftover minute/SMS overage lines
  next = next.replace(
    /\$30\/mo for phone calls \(first 20 minutes free, then \$0\.50\/min\)/gi,
    "Associate ($99/mo) for phone and email coverage",
  );
  next = next.replace(/first 20 minutes free, then \$0\.50\/min/gi, "included in Associate and Professional plans");
  next = next.replace(/first 100 SMS free, then \$0\.10\/SMS/gi, "included on Professional and Enterprise");
  next = next.replace(/\$0\.50\/min/g, "$99/mo Associate plans");
  next = next.replace(/\$0\.10\/SMS/g, "Professional plan SMS channels");
  next = next.replace(/\$0\.25\/min/g, "plan-included video minutes");

  // Practice stack examples that cited $80–$170 unit stacks
  next = next.replace(
    /A practice with Live Chat, Phone Calls, and an EMR integration[^.]*\./gi,
    siteGlueAiEmployeePricingExamplePhoneScheduling(),
  );

  // Footer link rows that still mix Try It Free
  if (
    (next.includes(`[${LEGACY_SIGNUP_CTA_LABEL}]`) ||
      next.includes(`[${SITEGLUE_SIGNUP_CTA_LABEL}]`)) &&
    next.includes("[Try It Free]")
  ) {
    next = next.replace(/\[Try It Free\]\([^)]+\)/g, SIGNUP_LINK_MARKDOWN);
  }

  return next;
}

export function rewriteBlogPricingMeta(text: string | undefined): string | undefined {
  if (!text) return text;
  let next = text;
  next = next.replace(/unit-based pricing/gi, "SiteGlue Agent plan pricing");
  next = next.replace(/unit-based AI pricing/gi, "SiteGlue Agent plan pricing");
  next = next.replace(/unit-based/gi, "plan-based");
  next = next.replace(/Try It Free/gi, SITEGLUE_SIGNUP_CTA_LABEL);
  next = next.replace(new RegExp(LEGACY_SIGNUP_CTA_LABEL, "g"), SITEGLUE_SIGNUP_CTA_LABEL);
  next = next.replace(/\$0\.50\/min/g, "$99/mo Associate");
  next = next.replace(/\$0\.10\/SMS/g, "Professional SMS channels");
  next = next.replace(/pay-per-use/gi, "plan-based");
  next = next.replace(/~\$80\/month/gi, "$99/mo (Associate)");
  next = next.replace(/~\$170\/month/gi, "$299/mo (Professional)");
  return next;
}

/** Convenience for adapters that need a standard footer. */
export function siteGlueBlogPricingFooter(): string {
  return siteGlueAiEmployeePricingFooterLinksMarkdown();
}
